import React from 'react';
import { Card, Row, Col, Typography, Table, Progress, Statistic } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * TimeOfDayAnalysis Component
 * Displays performance statistics segmented by time of day from Transport for NSW Trip Passing Time Reports
 */
const TimeOfDayAnalysis = ({ reportData }) => {
  // If no data, show empty state
  if (!reportData || !reportData.timeOfDayAnalysis || !reportData.timeOfDayAnalysis.periods) {
    return (
      <Card title="Time of Day Analysis">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No time of day analysis available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  const { timeOfDayAnalysis } = reportData;

  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // Transform the periods data for table display
  const timePeriodsData = timeOfDayAnalysis.periods.map((periodData, index) => ({
    key: `period-${index}`,
    period: periodData.label || periodData.period,
    totalTrips: periodData.totalTrips || 0,
    overallOTR: periodData.otrPercentage || 0,
    totalStops: periodData.totalStops || 0,
    onTimeCount: periodData.onTimeCount || 0,
    // Add estimated values for missing fields
    firstStopOTR: periodData.firstStopOTR || periodData.otrPercentage || 0,
    middleStopOTR: periodData.middleStopOTR || periodData.otrPercentage || 0,
    lastStopOTR: periodData.lastStopOTR || periodData.otrPercentage || 0,
    earlyPercentage: periodData.earlyPercentage || 0,
    latePercentage: periodData.latePercentage || 0,
    veryLatePercentage: periodData.veryLatePercentage || 0,
    averageVariance: periodData.averageVariance || 0
  })).sort((a, b) => {
    // Sort by time period order (Morning first, then Midday, etc.)
    const periodOrder = {
      'Morning Peak (6-10am)': 1,
      'Midday (10am-3pm)': 2,
      'Afternoon Peak (3-7pm)': 3,
      'Evening (7-12am)': 4,
      'Night (12-6am)': 5
    };
    return (periodOrder[a.period] || 99) - (periodOrder[b.period] || 99);
  });

  // Define table columns
  const columns = [
    {
      title: 'Time Period',
      dataIndex: 'period',
      key: 'period',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallOTR',
      key: 'overallOTR',
      render: value => (
        <Progress 
          percent={parseFloat(value.toFixed(2))} 
          size="small" 
          status={value >= 90 ? 'success' : (value >= 80 ? 'normal' : value >= 70 ? 'active' : 'exception')}
          format={percent => `${percent}%`}
        />
      ),
    },
    {
      title: 'Stops',
      dataIndex: 'totalStops',
      key: 'totalStops',
    },
    {
      title: 'On-Time',
      dataIndex: 'onTimeCount',
      key: 'onTimeCount',
    }
  ];

  // Find best and worst time periods by OTR
  const sortedByOTR = [...timePeriodsData].sort((a, b) => b.overallOTR - a.overallOTR);
  const bestPeriod = sortedByOTR.length > 0 ? sortedByOTR[0] : null;
  const worstPeriod = sortedByOTR.length > 0 ? sortedByOTR[sortedByOTR.length - 1] : null;

  // Calculate overall OTR for peak and off-peak
  const peakPeriods = timePeriodsData.filter(p => 
    p.period.includes('Morning Peak') || p.period.includes('Afternoon Peak')
  );
  const offPeakPeriods = timePeriodsData.filter(p => 
    !p.period.includes('Morning Peak') && !p.period.includes('Afternoon Peak')
  );
  
  // Calculate weighted averages for OTR percentages
  const calculateWeightedOTR = (periods) => {
    if (periods.length === 0) return 0;
    const totalStops = periods.reduce((sum, p) => sum + p.totalStops, 0);
    if (totalStops === 0) return 0;
    return periods.reduce((sum, p) => sum + (p.overallOTR * p.totalStops), 0) / totalStops;
  };
  
  const peakOTR = calculateWeightedOTR(peakPeriods);
  const offPeakOTR = calculateWeightedOTR(offPeakPeriods);
  
  // Count total stops
  const peakStops = peakPeriods.reduce((sum, p) => sum + p.totalStops, 0);
  const offPeakStops = offPeakPeriods.reduce((sum, p) => sum + p.totalStops, 0);

  return (
    <div className="time-of-day-analysis">
      <Card 
        title={<Title level={4}>Time of Day Performance</Title>} 
        className="analysis-card"
      >
        {/* Period performance summary */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title="Peak vs Off-Peak">
              <Row align="middle" justify="space-between">
                <Col span={11}>
                  <Statistic
                    title="Peak OTR"
                    value={peakOTR}
                    precision={1}
                    suffix="%"
                    valueStyle={{ fontSize: 20 }}
                  />
                  <Text type="secondary">{peakStops} stops</Text>
                </Col>
                <Col span={2} style={{ textAlign: 'center' }}>
                  <Text style={{ fontSize: 20 }}>vs</Text>
                </Col>
                <Col span={11}>
                  <Statistic
                    title="Off-Peak OTR"
                    value={offPeakOTR}
                    precision={1}
                    suffix="%"
                    valueStyle={{ fontSize: 20 }}
                  />
                  <Text type="secondary">{offPeakStops} stops</Text>
                </Col>
              </Row>
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <Text strong>
                  Difference: {Math.abs(peakOTR - offPeakOTR).toFixed(1)}%
                  {peakOTR > offPeakOTR ? ' (Peak better)' : peakOTR < offPeakOTR ? ' (Off-Peak better)' : ' (Equal)'}
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={<span><ClockCircleOutlined /> Best Time Period</span>}>
              {bestPeriod && (
                <>
                  <Title level={4} style={{ margin: 0, color: '#3f8600' }}>{bestPeriod.period}</Title>
                  <Progress 
                    percent={bestPeriod.overallOTR?.toFixed(2)} 
                    size="small" 
                    status="success"
                    format={percent => `${percent}%`}
                    style={{ marginTop: 8, marginBottom: 8 }}
                  />
                  <Text type="secondary">
                    {bestPeriod.totalStops} stops, {bestPeriod.onTimeCount} on-time
                  </Text>
                </>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={<span><ClockCircleOutlined /> Worst Time Period</span>}>
              {worstPeriod && (
                <>
                  <Title level={4} style={{ margin: 0, color: '#cf1322' }}>{worstPeriod.period}</Title>
                  <Progress 
                    percent={worstPeriod.overallOTR?.toFixed(2)} 
                    size="small" 
                    status={worstPeriod.overallOTR < 70 ? 'exception' : 'active'}
                    format={percent => `${percent}%`}
                    style={{ marginTop: 8, marginBottom: 8 }}
                  />
                  <Text type="secondary">
                    {worstPeriod.totalStops} stops, {worstPeriod.onTimeCount} on-time
                  </Text>
                </>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title="Time Distribution">
              {timePeriodsData.map((period) => (
                <div key={period.key} style={{ marginBottom: 5 }}>
                  <Text style={{ display: 'inline-block', width: '120px' }}>{period.period.replace(/ \([^)]+\)/, '')}:</Text>
                  <Progress 
                    percent={(period.totalStops / (peakStops + offPeakStops) * 100)?.toFixed(2) || 0} 
                    size="small" 
                    showInfo={false}
                    strokeColor={period.overallOTR >= 80 ? '#52c41a' : period.overallOTR >= 70 ? '#faad14' : '#f5222d'}
                  />
                  <Text style={{ marginLeft: 8 }}>{period.totalStops}</Text>
                </div>
              ))}
            </Card>
          </Col>
        </Row>

        {/* Time of day performance table */}
        <Table 
          dataSource={timePeriodsData}
          columns={columns}
          pagination={false}
        />

        {/* Peak vs Off-Peak Analysis */}
        <Card 
          title="Peak vs Off-Peak Analysis" 
          size="small" 
          style={{ marginTop: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12}>
              <Progress
                type="dashboard"
                width={120}
                percent={parseFloat(peakOTR.toFixed(1))}
                format={percent => `${percent}%`}
                status={peakOTR >= 80 ? 'success' : (peakOTR >= 70 ? 'normal' : 'exception')}
              />
              <div style={{ marginTop: 8 }}>
                <Title level={5}>Peak Time Performance</Title>
                <Text>
                  Peak periods (morning and afternoon rush hours) have an overall OTR of {formatPercent(peakOTR)}.
                  This is {peakOTR > offPeakOTR ? 'better' : 'worse'} than off-peak times by {Math.abs(peakOTR - offPeakOTR).toFixed(1)} percentage points.
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Progress
                type="dashboard"
                width={120}
                percent={parseFloat(offPeakOTR.toFixed(1))}
                format={percent => `${percent}%`}
                status={offPeakOTR >= 80 ? 'success' : (offPeakOTR >= 70 ? 'normal' : 'exception')}
              />
              <div style={{ marginTop: 8 }}>
                <Title level={5}>Off-Peak Performance</Title>
                <Text>
                  Off-peak periods (midday, evening, and night) have an overall OTR of {formatPercent(offPeakOTR)}.
                  {offPeakOTR > peakOTR 
                    ? ' Off-peak performance is better, suggesting less traffic congestion impacts service.' 
                    : ' Peak performance is better, which is unusual as off-peak typically has less congestion.'}
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      </Card>
    </div>
  );
};

export default TimeOfDayAnalysis; 